import React from 'react';
import { useTranslation } from 'react-i18next';

import { Panel, Field, Button } from '../../../../components';
import type { SettingsProfile } from '../../../../mock-data/types';
import {
  Form,
  ReadOnly,
  ReadOnlyLabel,
  ReadOnlyBox,
  ReadOnlyValue,
  Hint,
  SaveError,
  Footer,
} from './ProfileSection.styles';

/** Editable profile field keys (email is shown read-only above — it's auth-managed). */
const FIELD_KEYS: Exclude<keyof SettingsProfile, 'email'>[] = ['firstName', 'lastName', 'company', 'address'];

export type ProfileSectionProps = {
  values: SettingsProfile;
  dirty: boolean;
  saving: boolean;
  saveError: string | null;
  justSaved: boolean;
  onChange: (key: Exclude<keyof SettingsProfile, 'email'>, value: string) => void;
  onSave: () => void;
};

export const ProfileSection = ({
  values,
  dirty,
  saving,
  saveError,
  justSaved,
  onChange,
  onSave,
}: ProfileSectionProps): React.ReactElement => {
  const { t } = useTranslation();
  const saveLabel = saving ? t('Saving…') : justSaved ? t('Saved ✓') : t('Save changes');

  // Editable profile fields, labels translated reactively at render.
  const FIELD_LABELS: Record<Exclude<keyof SettingsProfile, 'email'>, string> = {
    firstName: t('First Name'),
    lastName: t('Last Name'),
    company: t('Company'),
    address: t('Address'),
  };

  return (
    <Panel>
      <Form>
        <ReadOnly>
          <ReadOnlyLabel text="Email address" tag="span" />
          <ReadOnlyBox>
            <ReadOnlyValue text={values.email || '-'} tag="span" />
          </ReadOnlyBox>
          <Hint text="Your email is managed by your account and can't be changed here." tag="span" />
        </ReadOnly>
        {FIELD_KEYS.map((key) => (
          <Field
            key={key}
            label={FIELD_LABELS[key]}
            value={values[key]}
            onChange={(value) => onChange(key, value)}
          />
        ))}
        {saveError ? <SaveError text={saveError} tag="span" /> : null}
        <Footer>
          <Button label={saveLabel} fullWidth disabled={!dirty || saving} onClick={onSave} />
        </Footer>
      </Form>
    </Panel>
  );
};

export default ProfileSection;
